import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { BUSINESS_TYPES } from "@/lib/businessTypes";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BillingSection from "@/components/app/BillingSection";

export default function Settings() {
  const { user, setUser } = useAuth();
  const [bizName, setBizName] = useState("");
  const [bizType, setBizType] = useState("");
  const [bizPhone, setBizPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setBizName(user.business_name || "");
      setBizType(user.business_type || "");
      setBizPhone((user.business_phone || "").replace(/^\+91/, ""));
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", { business_name: bizName, business_type: bizType, business_phone: bizPhone });
      setUser(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Workspace</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Settings</h1>
      </div>

      <div className="nb-card p-6 space-y-5">
        <h2 className="font-heading text-xl font-black">Business Profile</h2>
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Business Name</Label>
          <Input data-testid="settings-biz-name" className="nb-input" value={bizName} onChange={(e) => setBizName(e.target.value)} placeholder="e.g. Sharma RO Services" />
        </div>
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Business Contact Number</Label>
          <Input data-testid="settings-biz-phone" className="nb-input" type="tel" inputMode="numeric" value={bizPhone} onChange={(e) => setBizPhone(e.target.value)} placeholder="e.g. 9819000000" />
          <p className="text-xs text-[#4A4A4A] font-semibold mt-1">Shown to customers in WhatsApp reminders so they can call you.</p>
        </div>
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Primary Business Type</Label>
          <Select value={bizType} onValueChange={setBizType}>
            <SelectTrigger data-testid="settings-biz-type" className="nb-input">
              <SelectValue placeholder="Choose type" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map((b) => (<SelectItem key={b} value={b}>{b}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <button data-testid="settings-save" onClick={save} disabled={saving} className="nb-btn bg-[#4361EE] text-white px-5 py-2.5">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="nb-card p-6 space-y-3">
        <h2 className="font-heading text-xl font-black">Account</h2>
        <div className="flex items-center gap-4">
          {user?.picture && <img src={user.picture} alt="" className="w-14 h-14 border-2 border-black rounded-full object-cover" />}
          <div>
            <p className="font-bold">{user?.name}</p>
            <p className="text-sm text-[#4A4A4A] font-semibold">{user?.email}</p>
          </div>
        </div>
      </div>

      <BillingSection />

      <div className="nb-card p-6 bg-[#FFD166]/50">
        <h2 className="font-heading text-xl font-black mb-2">Messaging — Meta WhatsApp Cloud API</h2>
        <p className="text-sm font-semibold text-[#111111]">
          Configure `META_PHONE_NUMBER_ID`, `META_ACCESS_TOKEN`, `META_APP_SECRET`, `META_VERIFY_TOKEN` in the backend `.env` to enable live sending. Point Meta&apos;s webhook to <span className="font-mono text-xs bg-white border-2 border-black px-1 py-0.5 rounded">/api/whatsapp/webhook</span> to auto-mark-serviced on customer replies.
        </p>
      </div>
    </div>
  );
}
